import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { inventarioApi } from '@/services/inventarioApi';
import { almacenesApi } from '@/services/almacenesApi';
import { resolveImageUrl } from '@/utils/formatters';
import type { TipoItem } from '@/types/inventario';

const TIPO_LABELS: Record<TipoItem, string> = {
  PRODUCTO: 'Gabinetes',
  PANTALLA_MODULAR: 'Pantallas Modulares',
  CONTROLADOR: 'Controladores',
  FUENTE: 'Fuentes',
  TARJETA_RECEPTORA: 'Tarjetas Receptoras',
  OTRO: 'Otros',
};

const TIPOS: TipoItem[] = [
  'PRODUCTO',
  'PANTALLA_MODULAR',
  'CONTROLADOR',
  'FUENTE',
  'TARJETA_RECEPTORA',
  'OTRO',
];

export function StockView() {
  const [almacenId, setAlmacenId] = useState<number | ''>('');
  const [itemType, setItemType] = useState<TipoItem | ''>('');
  const [q, setQ] = useState('');

  const { data: almacenes = [] } = useQuery({
    queryKey: ['almacenes'],
    queryFn: () => almacenesApi.findAll(true),
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['inventario-stock', almacenId, itemType, q],
    queryFn: () =>
      inventarioApi.stock({
        almacenId: almacenId === '' ? null : almacenId,
        itemType: itemType === '' ? null : itemType,
        q: q || null,
      }),
  });

  const totalUnidades = useMemo(
    () => rows.reduce((acc, r) => acc + r.cantidad, 0),
    [rows],
  );
  const stockBajoCount = useMemo(() => rows.filter((r) => r.stockBajo).length, [rows]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Código o nombre"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Almacén</label>
          <select
            value={almacenId}
            onChange={(e) => setAlmacenId(e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todos</option>
            {almacenes.map((a) => (
              <option key={a.id} value={a.id}>{a.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
          <select
            value={itemType}
            onChange={(e) => setItemType((e.target.value as TipoItem) || '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todas</option>
            {TIPOS.map((t) => (
              <option key={t} value={t}>{TIPO_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-3 text-xs text-gray-500">
          <span><strong className="text-gray-900 text-base">{rows.length}</strong> filas</span>
          <span><strong className="text-gray-900 text-base">{totalUnidades}</strong> ud</span>
          {stockBajoCount > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <AlertTriangle size={14} /> <strong>{stockBajoCount}</strong> bajo
            </span>
          )}
        </div>
      </div>

      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Imagen</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ítem</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Categoría</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Almacén</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cantidad</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Umbral</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Sin resultados</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.stockId} className={`border-b border-gray-100 hover:bg-gray-50 ${r.stockBajo ? 'bg-amber-50/40' : ''}`}>
                  <td className="px-4 py-2.5">
                    {r.item.imagen ? (
                      <img src={resolveImageUrl(r.item.imagen)!} alt={r.item.nombre} className="w-9 h-9 object-cover rounded-md border border-gray-200" />
                    ) : (
                      <div className="w-9 h-9 bg-gray-100 rounded-md border border-gray-200 flex items-center justify-center"><ImageIcon size={14} className="text-gray-400" /></div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm font-mono text-gray-700">{r.item.codigo ?? '—'}</td>
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{r.item.nombre}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{TIPO_LABELS[r.item.itemType]}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{r.almacen.nombre}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`text-sm font-semibold ${r.cantidad === 0 ? 'text-red-600' : r.stockBajo ? 'text-amber-700' : 'text-gray-900'}`}>
                      {r.cantidad}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs text-gray-500">{r.umbralStockBajo}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2">
        {isLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-gray-500">Sin resultados</p>
        ) : (
          rows.map((r) => (
            <div key={r.stockId} className={`bg-white rounded-xl border p-3 ${r.stockBajo ? 'border-amber-200' : 'border-gray-200'}`}>
              <div className="flex gap-3 items-center">
                {r.item.imagen ? (
                  <img src={resolveImageUrl(r.item.imagen)!} alt={r.item.nombre} className="w-10 h-10 object-cover rounded-md border" />
                ) : (
                  <div className="w-10 h-10 bg-gray-100 rounded-md border flex items-center justify-center"><ImageIcon size={16} className="text-gray-400" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.item.nombre}</p>
                  <p className="text-xs text-gray-500">{r.almacen.nombre} · {TIPO_LABELS[r.item.itemType]}</p>
                </div>
                <span className={`text-base font-semibold shrink-0 ${r.cantidad === 0 ? 'text-red-600' : r.stockBajo ? 'text-amber-700' : 'text-gray-900'}`}>
                  {r.cantidad}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
